import React from "react";
import { promotionAvatar } from "../lib/api";
import { ChampionCrown, useChampionId } from "./ChampionCrown";

/**
 * AvatarLayered — renders a foreground avatar (wrestler or player) with an
 * optional bottom visual layer showing the owner's Promotion logo.
 *
 * Layer order (bottom → top):
 *   1. Promotion backdrop (if `promo` is provided)
 *   2. Foreground avatar (`fgSrc`)
 *
 * The wrapper handles rounded corners / border / sizing via `className`.
 * Both layers are absolutely positioned within the wrapper so the visual
 * size is entirely controlled by the parent's dimensions.
 *
 * Props:
 *   fgSrc      — url of the foreground avatar image (required)
 *   promo      — { id, avatar_updated_at } (optional); when present, its logo
 *                is rendered as a background layer underneath the fg avatar
 *   className  — wrapper classes: sizing, rounded, border, shadow, cursor…
 *   style      — inline styles for the wrapper (e.g. custom border-color)
 *   onClick    — click handler on the wrapper
 *   alt        — alt text for the foreground image
 *   title      — hover tooltip on the wrapper
 *   testId     — data-testid for the wrapper
 *   fgClassName — extra classes for the fg <img> (e.g. `grayscale`)
 */
export default function AvatarLayered({
  fgSrc,
  promo,
  className = "",
  style,
  onClick,
  alt = "",
  title,
  testId,
  fgClassName = "",
  showCrown = false,
}) {
  const champId = useChampionId();
  const bg = promo?.id ? promotionAvatar(promo.id, promo.avatar_updated_at) : null;
  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={style}
      onClick={onClick}
      title={title}
      data-testid={testId}
    >
      {bg && (
        <img
          src={bg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-95"
          draggable={false}
        />
      )}
      <img
        src={fgSrc}
        alt={alt}
        className={`relative w-full h-full object-cover ${fgClassName}`}
        draggable={false}
      />
      {showCrown && champId && promo?.id === champId && <ChampionCrown />}
    </div>
  );
}
